<template>
  <section class="exp-card situation-step">
    <button type="button" class="step-header" :aria-expanded="props.open" @click="emits('toggle')">
      <span class="step-number" :class="{ done: props.done, live: props.live }">
        <i v-if="props.done" class="bi bi-check-lg"></i>
        <template v-else>{{ props.index + 1 }}</template>
      </span>
      <span class="step-title">
        <!-- <span class="step-label">{{ $t('app.experience.detail.step', { number: props.index + 1 }) }}</span> -->
        <span class="step-name">{{ content.name }}</span>
      </span>
      <span class="step-badge hide-sm" :class="{ done: props.done, live: props.live }">{{ badge }}</span>
      <i class="bi bi-chevron-down chevron" :class="{ open: props.open }"></i>
    </button>
    <div v-if="props.open" ref="stepBody" class="step-body">
      <p v-if="content.instructions" v-safe-html="content.instructions" class="instructions"></p>
      <div class="step-footer">
        <span class="persona">
          <img v-if="content.avatar" class="avatar" :src="content.avatar" :alt="content.persona"/>
          <div class="text">
            <span><strong>{{ content.persona }}</strong><span v-if="content.role" class="role"> · {{ content.role }}</span></span>
          </div>
        </span>
        <template v-if="!props.finished">
          <button v-if="props.live" type="button" class="live-button" @click="emits('showConversation')">
            <span class="pulse-dot"></span>{{ $t('app.experience.detail.liveConversation') }}
          </button>
          <Button v-else-if="comingSoon" class="outline secondary icon icon-left" icon="bi-hourglass-split" :label="$t('app.experience.detail.comingSoon')" disabled />
          <Button v-else-if="props.done && canRetry" class="outline secondary icon icon-left" icon="bi-arrow-repeat" :label="$t('app.experience.detail.retryExercise')" :disabled="props.locked" v-tooltip="lockedTooltip" @click="emits('start')" />
          <Button v-else-if="props.done" class="outline secondary icon icon-left" icon="bi-check-lg" :label="$t('app.experience.detail.exerciseDone')" disabled />
        <Button v-else class="primary icon icon-left" icon="bi-camera-video" :label="$t('app.experience.detail.talkTo', { name: content.persona })" :disabled="props.locked" v-tooltip="lockedTooltip" @click="emits('start')" />
        </template>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { AssessmentSituation } from '@/types/campaign';
import { useSituationContent } from './useSituationContent';

const props = defineProps<{
  situation: AssessmentSituation,
  index: number,
  open: boolean,
  done: boolean,
  live: boolean,
  locked: boolean,
  finished: boolean,
}>();

const emits = defineEmits(['toggle', 'start', 'showConversation']);

const { t } = useI18n();

const content = useSituationContent(() => props.situation);
const comingSoon = computed(() => props.situation?.ui?.available === false);
// max_attempts: 1 = single attempt, undefined/null/0 = unlimited; the backend enforces the limit
const canRetry = computed(() => props.situation.max_attempts !== 1);

const stepBody = ref<HTMLElement | null>(null);

// Center the instructions on screen when the step is expanded (top-aligned if taller than the viewport)
watch(() => props.open, async (open) => {
  if (!open) return;
  await nextTick();
  const el = stepBody.value;
  if (!el) return;
  const block = el.offsetHeight > window.innerHeight ? 'start' : 'center';
  el.scrollIntoView({ behavior: 'smooth', block });
});

// Another situation's conversation is live: it must be ended before starting this one
const lockedTooltip = computed(() => props.locked ? t('app.experience.detail.finishCurrentFirst') : null);

const badge = computed(() => {
  if (props.live) return t('app.experience.detail.badges.live');
  if (props.done) return t('app.experience.detail.badges.done');
  return t('app.experience.detail.badges.todo');
});
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";
@import "../experience.scss";

.situation-step {
  .step-header {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 16px 20px;
    background: none;
    border: none;
    color: inherit;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
  }

  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid var(--light-grey-color);
    background-color: var(--light-white-color);
    font-size: 13px;
    font-weight: 700;

    &.done {
      border-color: $green-color;
      background-color: $green-color;
      color: $white-color;
    }

    &.live {
      border-color: $primary-color;
      background-color: $primary-color;
      color: $secondary-color;
    }
  }

  .step-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex-grow: 1;
    min-width: 0;

    .step-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .4px;
      color: var(--grey-50-color);
    }

    .step-name {
      font-size: 15px;
      font-weight: 700;
    }
  }

  .step-badge {
    flex-shrink: 0;
    padding: 4px 10px;
    border-radius: 12px;
    border: 1px solid var(--light-grey-color);
    background-color: var(--light-white-color);
    font-size: 11px;
    font-weight: 600;

    &.done {
      border-color: $green-200-color;
      background-color: $green-50-color;
      color: $green-800-color;
    }

    &.live {
      border-color: $primary-color;
      background-color: $primary-100-color;
      color: $primary-900-color;
    }
  }

  .chevron {
    flex-shrink: 0;
    font-size: 17px;
    transition: transform .2s;

    &.open {
      transform: rotate(180deg);
    }
  }

  .step-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 0 20px 20px 66px;

    .instructions {
      margin: 0;
      font-size: 14px;
      line-height: 1.6;
      white-space: pre-line;
    }
  }

  .step-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--light-grey-color);

    .persona {
      display: flex;
      gap: 10px;
      align-items: center;
      font-size: 13px;

      .text {
        display: flex;
        flex-direction: column;
        align-items: baseline;

        .timing {
          color: var(--grey-50-color);
        }
      }


      strong {
        font-weight: 600;
      }

      .role {
        color: var(--grey-50-color);
      }
    }

    .avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background-color: $secondary-color;
      color: $white-color;
      font-weight: 600;
      object-fit: cover;
      border: 1px solid var(--secondary-color);
    }
  }

  .live-button {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 18px;
    border: none;
    border-radius: 6px;
    background-color: $secondary-color;
    color: $white-color;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #FF5A4E;
    animation: experience-pulse 1.4s ease-in-out infinite;
  }

  @media (max-width: 760px) {
    .hide-sm {
      display: none;
    }

    .step-body {
      padding-left: 20px;
    }
  }
}

@keyframes experience-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .3; }
}
</style>
